import { useEffect, useState } from "react";
import { Navigate, Outlet, useNavigate, NavLink } from "react-router-dom";
import { getToken, clearToken, adminClient } from "./api";
import { Button } from "@/components/ui/button";
import { Truck, LogOut, Shirt, Recycle, Calendar, Mail, Package, LayoutDashboard, Star, Image as ImageIcon, DollarSign, SplitSquareHorizontal, CalendarDays, AlertTriangle, Sofa } from "lucide-react";

export default function AdminShell() {
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [openClaims, setOpenClaims] = useState(0);

  useEffect(() => {
    if (!getToken()) return;
    let mounted = true;
    const load = () => {
      adminClient.get("/admin/alerts?limit=100")
        .then(({ data }) => { if (mounted) setUnread((data.items || []).filter((a) => !a.read).length); })
        .catch(() => {});
      adminClient.get("/admin/claims?status=open")
        .then(({ data }) => { if (mounted) setOpenClaims((data.items || []).length); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    const handler = () => load();
    window.addEventListener("hp:alerts-changed", handler);
    return () => { mounted = false; clearInterval(t); window.removeEventListener("hp:alerts-changed", handler); };
  }, []);

  if (!getToken()) return <Navigate to="/admin/login" replace />;

  const links = [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, badge: unread },
    { to: "/admin/today", label: "Today", icon: CalendarDays },
    { to: "/admin/hero-images", label: "Hero Images", icon: ImageIcon },
    { to: "/admin/before-after", label: "Before After", icon: SplitSquareHorizontal },
    { to: "/admin/merch", label: "Merch", icon: Shirt },
    { to: "/admin/marketplace", label: "Used Goods", icon: Recycle },
    { to: "/admin/reviews", label: "Reviews", icon: Star },
    { to: "/admin/pricing", label: "Pricing", icon: DollarSign },
    { to: "/admin/furniture-depot", label: "Furniture Depot", icon: Sofa },
    { to: "/admin/bookings", label: "Bookings", icon: Calendar },
    { to: "/admin/inquiries", label: "Inquiries", icon: Mail },
    { to: "/admin/orders", label: "Orders", icon: Package },
    { to: "/admin/claims", label: "Claims", icon: AlertTriangle, badge: openClaims },
  ];

  const logout = () => {
    clearToken();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="bg-[#0C2E2B] border-b border-[#0F3B37] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#FFDF00] grid place-items-center">
              <Truck className="w-5 h-5 text-[#0C2E2B]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-white text-sm leading-none">HARVEY'S PLACE</div>
              <div className="font-tactical text-[9px] uppercase tracking-[0.25em] text-[#FFDF00]">Admin Console</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site-link" className="text-xs font-bold text-white/70 hover:text-white">View Site →</a>
            <Button
              data-testid="admin-logout-btn"
              variant="outline"
              size="sm"
              onClick={logout}
              className="rounded-md text-xs bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
            >
              <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-bold whitespace-nowrap transition-colors ${
                  isActive ? "bg-[#0C2E2B] text-[#FFDF00]" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <l.icon className="w-4 h-4" /> {l.label}
              {l.badge > 0 && (
                <span data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-badge`} className="ml-auto bg-red-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {l.badge > 99 ? "99+" : l.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
